'use client';

import React from 'react';
import styles from './Watchlist.module.css';

/**
 * @uiComponent
 * @name SegmentTabs
 * @description A tabbed navigation component for switching between different market segments 
 * like Popular, Cash (Equity), and F&O (Futures & Options) on the Groww platform.
 * Provides visual feedback for the currently active segment.
 * @location Top section of the Watchlist component when "All" tab is selected
 * @prop activeSegment The currently selected segment identifier
 * @prop onSegmentChange Callback function triggered when user selects a different segment
 * @prop segments Optional array of segment names to display (defaults to ['CASH', 'FNO'])
 */

/**
 * @uiWorkflow
 * @name Switch Between Market Segments on Groww
 * @description How to navigate between different market categories in the watchlist
 * @platform groww
 * @step 1 Ensure you are on the "All" tab in the Watchlist [component: Watchlist] [action: click]
 * @step 2 Locate the segment tabs below the main tabs [component: SegmentTabs] [action: observe]
 * @step 3 Click on "POPULAR" to view trending and popular stocks [component: SegmentTabs] [action: click]
 * @step 4 Click on "CASH" to browse equity and BE series stocks [component: SegmentTabs] [action: click]
 * @step 5 Click on "F&O" to view futures and options instruments [component: SegmentTabs] [action: click]
 * @step 6 Notice the active segment is highlighted with different styling [component: SegmentTabs] [action: observe]
 * @step 7 The watchlist content updates automatically based on selected segment [component: Watchlist] [action: observe]
 */

interface SegmentTabsProps {
  activeSegment: string;
  onSegmentChange: (segment: string) => void;
  segments?: string[];
}

export default function SegmentTabs({ activeSegment, onSegmentChange, segments = ['CASH', 'FNO'] }: SegmentTabsProps) {
  return (
    <div className={styles.segmentTabs}>
      {segments.map(segment => (
        <div
          key={segment}
          className={`${styles.segmentChip} ${
            segment === activeSegment ? styles.activeSegment : ''
          }`}
          onClick={() => onSegmentChange(segment)}
        >
          {segment}
        </div>
      ))}
    </div>
  );
} 